import React, { useEffect, useState } from 'react';
import {
    Box, Container, Heading, Text, VStack, HStack, Button, SimpleGrid, Image, Badge, Icon, Flex,
    Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, Wrap, WrapItem, Tag, Table, Thead, Tbody, Tr, Th, Td,
} from '@chakra-ui/react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
    FiCheckCircle, FiClock, FiMapPin, FiPhone, FiTruck, FiDroplet, FiWind, FiPackage,
    FiCalendar, FiMessageSquare, FiSun, FiAlertCircle, FiHome,
} from 'react-icons/fi';
import { smsBody, smsHref, textCode } from '../Components/SiteLanding/textOrderLinks';
import { priceOffer } from '../utils/priceOffer';

const LAUNDRY_ID = '1';
const PHONE_DISPLAY = '(512) 782-0428';
const PHONE_TEL = '+15127820428';
const ADDRESS = '900 E Palm Valley Blvd, Suite 1006-1007, Round Rock, TX 78664';
const CANONICAL = 'https://roundrocklaundry.com/wash-and-fold';

const IMG = (id, w) => `https://images.unsplash.com/${id}?w=${w}&q=75&auto=format&fit=crop`;
const HERO_IMG = 'photo-1517677208171-0bc6725a3e60';
const FOLD_IMG = 'photo-1582735689369-4fe89db7114c';
const HOME_IMG = 'photo-1545173168-9f1947eebb55';

const AREAS = [
    'Round Rock', 'Brushy Creek', 'Teravista', 'Forest Creek', 'Paloma Lake', 'Avery Ranch',
    'Cedar Park', 'Georgetown', 'Hutto', 'Pflugerville', 'Wells Branch', 'North Austin',
];

const PLANT_STEPS = [
    { icon: FiPackage, title: 'We open the bag', text: 'Everyday clothes stay in wash and fold. Comforters, dry-clean-only pieces, and leather are pulled out and handled on their own.' },
    { icon: FiSun, title: 'We sort', text: 'Lights and darks are washed apart. A note for delicates, fragrance-free, or no softener is followed on that load.' },
    { icon: FiDroplet, title: 'We wash', text: 'Commercial machines give the clothes room to move, so detergent rinses out instead of sitting in the fabric.' },
    { icon: FiWind, title: 'We dry', text: 'Loads are dried until they are dry, then cooled. Damp clothes are what make a bag smell musty the next day.' },
    { icon: FiCheckCircle, title: 'We fold', text: 'Shirts, towels, and socks are folded into stacks you can put straight into a drawer.' },
    { icon: FiTruck, title: 'We hand it back', text: 'Pick it up at the store, or we deliver it to your door. You choose when you book.' },
];

const HOME_SORT = [
    { title: 'Lights', text: 'Whites, pastels, and light grays. A new white tee can pick up dye from a red sock.' },
    { title: 'Darks', text: 'Jeans, black, and navy. Wash a brand-new pair of dark jeans alone the first time.' },
    { title: 'Towels', text: 'Keep towels out of the clothes load. Their lint sticks to everything else.' },
    { title: 'Workout clothes', text: 'Sweat holds odor. A separate load, and skip fabric softener, which traps smell in synthetic fabric.' },
];

const LEAVE_OUT = [
    'Comforters, duvets, and pillows — those need a bigger machine and a different price',
    'Anything labeled dry clean only',
    'Leather, suede, and pieces with heavy beading or glue-on trim',
    'A stain you have not mentioned — oil, ink, and wine set if they go through a hot dryer',
];

const HOW_OFTEN = [
    { item: 'Underwear, socks, workout clothes', often: 'After every wear' },
    { item: 'T-shirts and blouses', often: 'After one or two wears' },
    { item: 'Jeans and sweaters', often: 'After a few wears, unless they are dirty' },
    { item: 'Sheets', often: 'Every one to two weeks' },
    { item: 'Bath towels', often: 'Every three or four uses' },
    { item: 'Kitchen towels', often: 'Every few days — they hold food and grease' },
];

const FOLD = [
    { title: 'T-shirt', text: 'Fold the sides in so the sleeves disappear, then fold the shirt into thirds from the bottom.' },
    { title: 'Towel', text: 'Fold in thirds the long way, then in half. The finished stack stands up in a closet.' },
    { title: 'Jeans', text: 'Fold in half at the legs, then in thirds. Creases stay out of the thigh.' },
    { title: 'Socks', text: 'Pair them and fold once. Do not ball them — the elastic stretches out.' },
];

const FAQS = [
    { q: 'Do you offer wash and fold in Round Rock?', a: 'Yes. Eco Spin Round Rock Laundry washes, dries, and folds everyday clothes in Round Rock, TX. Drop a bag at the store or have us pick it up.' },
    { q: 'Do you pick up laundry?', a: 'Yes. We pick up laundry in Round Rock and nearby areas, wash and fold it, and deliver it back to you.' },
    { q: 'How do I schedule pickup?', a: 'Text (512) 782-0428 to schedule laundry pickup in Round Rock, TX. You can also book on this website.' },
    { q: 'Can I schedule by text?', a: 'Yes. Text (512) 782-0428 to schedule laundry pickup in Round Rock, TX.' },
    { q: 'How much is wash and fold?', a: 'Wash and fold is priced by the pound. New customers get 25% off with code FIRST25. Enter FIRST25 when you schedule the pickup.' },
    { q: 'What is wash and fold?', a: 'Wash and fold is everyday laundry done for you: clothes are washed, dried, and folded, then returned in a bag. You pay by the pound. It is not dry cleaning, and it is not the same as comforter cleaning.' },
    { q: 'How do you wash the clothes?', a: 'We sort lights and darks, wash them in commercial machines with room for the load to move, and dry them all the way through. Tell us if you want fragrance-free detergent or no fabric softener and that load is washed that way.' },
    { q: 'How should I sort laundry at home?', a: 'Four piles cover almost everything: lights, darks, towels, and workout clothes. Wash new dark jeans alone the first time. Keep towels out of the clothes washer so lint does not cover your shirts.' },
    { q: 'What should I leave out of a wash-and-fold bag?', a: 'Leave out comforters, dry-clean-only garments, leather, and anything with a stain you have not told us about. A hot dryer sets oil, ink, and wine.' },
    { q: 'How often should clothes be washed?', a: 'Underwear, socks, and workout clothes after every wear. T-shirts after one or two wears. Jeans and sweaters after a few wears unless they are soiled. Sheets every one to two weeks. Bath towels every three or four uses.' },
    { q: 'Do I have to use your wash and fold service?', a: 'No. This page is a wash-and-fold guide you can use at home. If you would rather hand the bag off, schedule a pickup or drop it at the store. We are open 24/7.' },
    { q: 'Is there a discount for new customers?', a: 'Yes. New customers get 25% off wash and fold with code FIRST25. Enter FIRST25 when you schedule the pickup.' },
    { q: 'Do you pick up and deliver?', a: 'Yes. Schedule a pickup online and we bring the folded clothes back. The delivery fee depends on distance and is shown before you confirm. You can also drop off at 900 E Palm Valley Blvd anytime.' },
];

const ACCENT = 'teal';
const SOLID = { bg: 'teal.600', color: 'white', _hover: { bg: 'teal.700', transform: 'translateY(-1px)', boxShadow: 'lg' } };

const TextToSchedule = ({ ordering, color = 'gray.700', iconColor = 'teal.500', testId }) => {
    if (!ordering?.keyword || !ordering?.smsNumber) return null;
    const code = textCode(ordering);
    return (
        <HStack as="a" href={smsHref(ordering.smsNumber, code)} spacing={1.5} color={color}
            data-testid={testId} _hover={{ textDecoration: 'underline' }}>
            <Icon as={FiMessageSquare} color={iconColor} />
            <Text fontSize="sm">
                Text to schedule — text{' '}
                <Text as="span" fontWeight="800" fontFamily="mono">{smsBody(code).trim()}</Text>
                {' '}to {ordering.smsNumberDisplay}
            </Text>
        </HStack>
    );
};

const SectionTitle = ({ eyebrow, title, sub }) => (
    <VStack spacing={3} textAlign="center" maxW="760px" mx="auto">
        {eyebrow && (
            <Text fontSize="sm" fontWeight="700" letterSpacing="0.12em" textTransform="uppercase" color={`${ACCENT}.600`}>
                {eyebrow}
            </Text>
        )}
        <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }} color="gray.800" lineHeight="short">{title}</Heading>
        {sub && <Text color="gray.600" fontSize={{ base: 'md', md: 'lg' }}>{sub}</Text>}
    </VStack>
);

const WashAndFoldPage = () => {
    const params = useParams();
    const laundryId = params.laundryId || LAUNDRY_ID;
    const orderHref = `/${laundryId}`;
    const [textOrdering, setTextOrdering] = useState(null);
    const [priceLabel, setPriceLabel] = useState('');
    const [offer, setOffer] = useState(null);

    useEffect(() => {
        let cancelled = false;
        axios.get(`${process.env.REACT_APP_AWS_API_URL}/api/laundry/get-info`, {
            params: { operation: 'getLaundryInfo', laundryId, isCustomer: true },
        }).then((res) => {
            if (cancelled) return;
            if (res.data?.textOrdering?.keyword) setTextOrdering(res.data.textOrdering);
            const svc = (res.data?.laundryServices || []).find((s) => {
                const name = (s.serviceName || '').toLowerCase();
                return name.includes('wash') && name.includes('fold');
            });
            const price = parseFloat(svc?.price);
            if (!Number.isNaN(price) && price > 0) setPriceLabel(`$${price.toFixed(2)} per pound`);
            setOffer(priceOffer({
                laundryId,
                services: res.data?.laundryServices || [],
                minWeightEnabled: res.data?.minWeightEnabled === true,
                recurringDiscount: res.data?.recurringDiscount || 0,
                deliveryFeeMode: res.data?.deliveryFeeMode,
                deliveryFeeFlat: res.data?.deliveryFeeFlat,
                deliveryFeeBase: res.data?.deliveryFeeBase,
                deliveryFeePerMile: res.data?.deliveryFeePerMile,
                deliveryFeeFreeRadiusMi: res.data?.deliveryFeeFreeRadiusMi,
                deliveryFeeTiers: res.data?.deliveryFeeTiers || [],
                dropoffWash: res.data?.dropoffWash || null,
                siteContent: {
                    city: 'Round Rock',
                    hours: [{ day: 'Mon-Sun', time: 'Open 24/7' }],
                    promoEnabled: true,
                    promoDiscount: '25',
                    promoCode: 'FIRST25',
                    ...(res.data?.siteContent || {}),
                },
            }));
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [laundryId]);

    const jsonLd = [
        {
            '@context': 'https://schema.org',
            '@type': 'Laundry',
            name: 'Eco Spin Laundry',
            url: 'https://roundrocklaundry.com/',
            telephone: PHONE_TEL,
            address: {
                '@type': 'PostalAddress',
                streetAddress: '900 E Palm Valley Blvd Suite 1006-1007',
                addressLocality: 'Round Rock',
                addressRegion: 'TX',
                postalCode: '78664',
                addressCountry: 'US',
            },
            areaServed: { '@type': 'City', name: 'Round Rock' },
            hasOfferCatalog: {
                '@type': 'OfferCatalog',
                name: 'Laundry Services',
                itemListElement: [
                    { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Wash and Fold Laundry' } },
                    { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Laundry Pickup and Delivery' } },
                ],
            },
        },
        {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: FAQS.map((f) => ({
                '@type': 'Question',
                name: f.q,
                acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
        },
    ];

    return (
        <Box bg="white" minH="100vh">
            <Helmet>
                <title>Wash &amp; Fold Laundry Pickup &amp; Delivery in Round Rock, TX | Eco Spin Laundry</title>
                <meta
                    name="description"
                    content="Wash & fold laundry pickup and delivery in Round Rock, TX. Text (512) 782-0428 to schedule pickup with Eco Spin Laundry."
                />
                <link rel="canonical" href={CANONICAL} />
                <meta property="og:title" content="Wash & Fold Laundry Pickup & Delivery in Round Rock, TX | Eco Spin Laundry" />
                <meta property="og:description" content="Wash & fold laundry pickup and delivery in Round Rock, TX. Text (512) 782-0428 to schedule pickup with Eco Spin Laundry." />
                <meta property="og:image" content={IMG(HERO_IMG, 1200)} />
                <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
            </Helmet>

            <Box bg="gray.900" color="white" py={2.5} px={4} textAlign="center" data-testid="waf-promo">
                <Text fontSize={{ base: 'sm', md: 'md' }}>
                    <Text as="span" fontWeight="800" color="orange.300">New customers: 25% off wash and fold.</Text>{' '}
                    Use code <Text as="span" fontFamily="mono" fontWeight="800">FIRST25</Text>
                </Text>
            </Box>

            <Box position="relative" overflow="hidden" bg="linear-gradient(135deg, #F0FDFA 0%, #E6FFFA 42%, #F7FAFC 100%)">
                <Container maxW="1200px" py={{ base: 12, md: 20 }} position="relative">
                    <Flex direction={{ base: 'column', md: 'row' }} align="center" gap={{ base: 10, md: 14 }}>
                        <VStack align={{ base: 'center', md: 'flex-start' }} spacing={6} flex="1" textAlign={{ base: 'center', md: 'left' }}>
                            <Badge colorScheme={ACCENT} fontSize="sm" px={3} py={1} borderRadius="full" textTransform="none">
                                Round Rock wash and fold
                            </Badge>
                            {(offer || priceLabel) && (
                                <Text fontSize={{ base: '3xl', md: '5xl' }} fontWeight="800" color="gray.900" lineHeight="1.05" data-testid="waf-price">
                                    {offer ? `${offer.headlineLead}${offer.priceText}` : `Wash and fold is ${priceLabel}.`}
                                </Text>
                            )}
                            {offer && (
                                <VStack spacing={0} align={{ base: 'center', md: 'flex-start' }}>
                                    {offer.highlights.map((line) => (
                                        <Text key={line} fontWeight="700" color="gray.800">{line}</Text>
                                    ))}
                                </VStack>
                            )}
                            <VStack spacing={0} align={{ base: 'center', md: 'flex-start' }}>
                                <Text fontSize={{ base: 'xl', md: '3xl' }} fontWeight="800" color="orange.500" lineHeight="1.1">
                                    25% off for new customers
                                </Text>
                                <Text fontSize={{ base: 'lg', md: '2xl' }} fontWeight="700" color="gray.800">
                                    Code <Text as="span" fontFamily="mono">FIRST25</Text>
                                </Text>
                            </VStack>
                            <Heading as="h1" fontSize={{ base: '4xl', md: '5xl' }} lineHeight="1.05" color="gray.800" letterSpacing="-0.03em">
                                Wash &amp; Fold Laundry Pickup in Round Rock, TX
                            </Heading>
                            <Text fontSize={{ base: 'lg', md: 'xl' }} color="gray.800" maxW="640px" data-testid="waf-text-plain">
                                Text{' '}
                                <Text as="a" href={smsHref(PHONE_TEL, '')} fontWeight="800" textDecoration="underline">
                                    (512) 782-0428
                                </Text>
                                {' '}to schedule laundry pickup in Round Rock, TX.
                            </Text>
                            <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600" maxW="560px">
                                We offer convenient wash-and-fold laundry pickup and delivery in Round Rock.
                                Our team will pick up your laundry, wash, dry, fold, and deliver it back to you.
                            </Text>
                            <VStack align={{ base: 'center', md: 'flex-start' }} spacing={3}>
                                <HStack spacing={3} flexWrap="wrap" justify={{ base: 'center', md: 'flex-start' }}>
                                    <Button as="a" href={orderHref} size="lg" {...SOLID} borderRadius="full" px={8} data-testid="waf-cta-hero">
                                        Schedule a pickup
                                    </Button>
                                    <Button as="a" href="#how-we-wash" size="lg" variant="outline" colorScheme="teal" borderRadius="full" px={8}>
                                        See how we wash
                                    </Button>
                                </HStack>
                                <TextToSchedule ordering={textOrdering} testId="waf-text-hero" />
                            </VStack>
                        </VStack>
                        <Box flex="1" w="100%">
                            <Image src={IMG(HERO_IMG, 900)} alt="A basket of clean folded laundry" borderRadius="2xl" objectFit="cover" h={{ base: '240px', md: '420px' }} w="100%" boxShadow="xl" />
                        </Box>
                    </Flex>
                </Container>
            </Box>

            <Container maxW="900px" py={{ base: 14, md: 20 }}>
                <SectionTitle
                    eyebrow="What it is"
                    title="Wash and fold is everyday laundry, finished"
                    sub="Shirts, towels, socks, sheets, and jeans. They go in a bag, come back clean, and land in a drawer. You are billed for the weight of the clothes, not a flat guess."
                />
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mt={10}>
                    {[
                        { title: 'Not dry cleaning', text: 'Wash and fold uses water and detergent. A “dry clean only” label does not belong in the bag.' },
                        { title: 'Not comforter cleaning', text: 'A queen comforter needs a different machine and a different price. Bedding has its own page.' },
                        { title: 'Ready to put away', text: 'The point of the fold is that you do not re-fold it on the bed when you get home.' },
                    ].map((c) => (
                        <Box key={c.title} p={6} borderRadius="xl" bg="gray.50">
                            <Heading as="h3" size="md" mb={2} color="gray.800">{c.title}</Heading>
                            <Text color="gray.600">{c.text}</Text>
                        </Box>
                    ))}
                </SimpleGrid>
                <Text textAlign="center" mt={6}>
                    <Text as="a" href={laundryId ? `/${laundryId}/comforter-cleaning` : '/comforter-cleaning'} color="teal.700" fontWeight="600" textDecoration="underline">
                        Comforter and duvet cleaning
                    </Text>
                </Text>
            </Container>

            <Box id="how-we-wash" bg="gray.50" py={{ base: 16, md: 24 }}>
                <Container maxW="1100px">
                    <SectionTitle
                        eyebrow="How we wash and fold"
                        title="What happens to the bag after you hand it over"
                        sub="Lights and darks are separated. Eco-friendly detergent is available. Fragrance-free is a note on the order, not a guess."
                    />
                    <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={8} mt={12}>
                        {PLANT_STEPS.map((s, i) => (
                            <VStack key={s.title} align="flex-start" spacing={3} bg="white" p={6} borderRadius="xl" boxShadow="sm">
                                <HStack>
                                    <Flex w={10} h={10} align="center" justify="center" borderRadius="full" bg={`${ACCENT}.600`} color="white" fontWeight="800">
                                        {i + 1}
                                    </Flex>
                                    <Icon as={s.icon} color={`${ACCENT}.600`} boxSize={5} />
                                </HStack>
                                <Heading as="h3" size="md" color="gray.800">{s.title}</Heading>
                                <Text color="gray.600">{s.text}</Text>
                            </VStack>
                        ))}
                    </SimpleGrid>
                </Container>
            </Box>

            <Container id="wash-at-home" maxW="1100px" py={{ base: 16, md: 24 }}>
                <SectionTitle
                    eyebrow="Use this at home"
                    title="How to wash and fold if you keep the bag"
                    sub="You do not have to book. The same habits are what make a home load come out even."
                />
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} mt={12} alignItems="center">
                    <Image src={IMG(HOME_IMG, 900)} alt="Clothes sorted into a washing machine" borderRadius="2xl" objectFit="cover" h={{ base: '220px', md: '360px' }} w="100%" />
                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={5}>
                        {HOME_SORT.map((s) => (
                            <Box key={s.title}>
                                <Heading as="h3" size="sm" color="gray.800" mb={1}>{s.title}</Heading>
                                <Text color="gray.600" fontSize="sm">{s.text}</Text>
                            </Box>
                        ))}
                    </SimpleGrid>
                </SimpleGrid>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} mt={12}>
                    <Box>
                        <Heading as="h3" size="md" mb={4} color="gray.800">Leave these out of the bag</Heading>
                        <VStack align="stretch" spacing={3}>
                            {LEAVE_OUT.map((t) => (
                                <HStack key={t} align="flex-start" spacing={3}>
                                    <Icon as={FiAlertCircle} color="orange.400" mt={1} />
                                    <Text color="gray.700">{t}</Text>
                                </HStack>
                            ))}
                        </VStack>
                    </Box>
                    <Box>
                        <Heading as="h3" size="md" mb={4} color="gray.800">A simple fold</Heading>
                        <VStack align="stretch" spacing={4}>
                            {FOLD.map((f) => (
                                <Box key={f.title}>
                                    <Text fontWeight="700" color="gray.800">{f.title}</Text>
                                    <Text color="gray.600" fontSize="sm">{f.text}</Text>
                                </Box>
                            ))}
                        </VStack>
                    </Box>
                </SimpleGrid>
            </Container>

            <Box bg="#F7FAFC" py={{ base: 16, md: 20 }}>
                <Container maxW="860px">
                    <SectionTitle eyebrow="How often" title="A practical wash schedule" sub="Washing less often is fine for jeans. It is not fine for gym clothes." />
                    <Box mt={8} bg="white" borderRadius="xl" overflow="hidden" boxShadow="sm">
                        <Table size="sm" variant="simple">
                            <Thead bg="gray.50">
                                <Tr>
                                    <Th>Item</Th>
                                    <Th>Wash</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {HOW_OFTEN.map((row) => (
                                    <Tr key={row.item}>
                                        <Td fontWeight="600" color="gray.800">{row.item}</Td>
                                        <Td color="gray.600">{row.often}</Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </Box>
                    <Text mt={4} fontSize="sm" color="gray.500" textAlign="center">
                        Blot a fresh stain. Do not rub it. Cold water for blood. Tell us about oil, ink, or wine before the dryer — heat sets those stains.
                    </Text>
                </Container>
            </Box>

            <Box py={{ base: 16, md: 20 }}>
                <Container maxW="1100px">
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12} alignItems="center">
                        <VStack align="flex-start" spacing={4}>
                            <Text fontSize="sm" fontWeight="700" letterSpacing="0.12em" textTransform="uppercase" color={`${ACCENT}.600`}>If you would rather not</Text>
                            <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }} color="gray.800" lineHeight="short">
                                Drop the bag off, or we pick it up
                            </Heading>
                            <Text color="gray.600" fontSize="lg">
                                The store at Palm Valley is open 24/7 for drop-off. Pickup and delivery covers Round Rock and nearby neighborhoods.
                                The delivery fee is shown before you confirm.
                            </Text>
                            <VStack align="flex-start" spacing={2}>
                                {['Priced by the pound of clothes', 'Lights and darks washed apart', 'Fragrance-free when you ask for it', 'Folded and packed to put away'].map((t) => (
                                    <HStack key={t}><Icon as={FiCheckCircle} color={`${ACCENT}.500`} /><Text>{t}</Text></HStack>
                                ))}
                            </VStack>
                            <Button as="a" href={orderHref} {...SOLID} borderRadius="full" px={8} data-testid="waf-cta-mid">Schedule a pickup</Button>
                        </VStack>
                        <Image src={IMG(FOLD_IMG, 900)} alt="Neatly folded stacks of laundry" borderRadius="2xl" objectFit="cover" h={{ base: '240px', md: '380px' }} w="100%" boxShadow="lg" />
                    </SimpleGrid>
                </Container>
            </Box>

            <Container maxW="1000px" py={{ base: 8, md: 12 }} textAlign="center">
                <SectionTitle eyebrow="Service area" title="Round Rock and nearby" />
                <Wrap justify="center" spacing={3} mt={8}>
                    {AREAS.map((a) => (
                        <WrapItem key={a}>
                            <Tag size="lg" borderRadius="full" variant="subtle" colorScheme={ACCENT} px={4} py={2}>
                                <Icon as={FiMapPin} mr={2} />{a}
                            </Tag>
                        </WrapItem>
                    ))}
                </Wrap>
            </Container>

            <Box bg="gray.50" py={{ base: 16, md: 20 }}>
                <Container maxW="820px">
                    <SectionTitle eyebrow="Questions" title="Wash and fold, in plain language" />
                    <Accordion allowToggle mt={10} bg="white" borderRadius="2xl" boxShadow="sm" overflow="hidden">
                        {FAQS.map((f) => (
                            <AccordionItem key={f.q} borderColor="gray.100">
                                <h3>
                                    <AccordionButton py={5} px={6} _hover={{ bg: 'gray.50' }}>
                                        <Box flex="1" textAlign="left" fontWeight="600" color="gray.800">{f.q}</Box>
                                        <AccordionIcon />
                                    </AccordionButton>
                                </h3>
                                <AccordionPanel px={6} pb={5} color="gray.600">{f.a}</AccordionPanel>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </Container>
            </Box>

            <Box bg="gray.900" color="white" py={{ base: 16, md: 20 }}>
                <Container maxW="800px" textAlign="center">
                    <VStack spacing={5}>
                        <VStack spacing={1} data-testid="waf-promo-footer">
                            <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="800" color="orange.300">New customers save 25%</Text>
                            <Heading as="h2" fontSize={{ base: '3xl', md: '5xl' }} lineHeight="short">
                                Use code <Text as="span" fontFamily="mono">FIRST25</Text>
                            </Heading>
                        </VStack>
                        <Icon as={FiHome} boxSize={8} color="teal.300" />
                        <Heading as="h3" fontSize={{ base: '2xl', md: '3xl' }}>Keep the guide. Or send the bag.</Heading>
                        <Text color="gray.300" fontSize="lg">
                            {priceLabel ? `Wash and fold is ${priceLabel}. ` : ''}
                            Schedule a pickup, drop off anytime, or use the sorting and folding steps above at home.
                        </Text>
                        <HStack spacing={4} flexWrap="wrap" justify="center">
                            <Button as="a" href={orderHref} size="lg" {...SOLID} borderRadius="full" px={10} data-testid="waf-cta-footer">
                                Schedule a pickup
                            </Button>
                            <Button as="a" href={`tel:${PHONE_TEL}`} size="lg" variant="outline" colorScheme="whiteAlpha" color="white" borderRadius="full" px={8} leftIcon={<FiPhone />}>
                                {PHONE_DISPLAY}
                            </Button>
                        </HStack>
                        <TextToSchedule ordering={textOrdering} color="gray.200" iconColor="teal.300" testId="waf-text-footer" />
                        <VStack spacing={1} pt={2} color="gray.400" fontSize="sm">
                            <HStack spacing={2}><Icon as={FiMapPin} /><Text>{ADDRESS}</Text></HStack>
                            <HStack spacing={2}><Icon as={FiClock} /><Text>Open 24/7 for drop-off</Text></HStack>
                            <HStack spacing={2}><Icon as={FiCalendar} /><Text as="a" href="#wash-at-home" textDecoration="underline">Jump to the at-home guide</Text></HStack>
                        </VStack>
                    </VStack>
                </Container>
            </Box>
        </Box>
    );
};

export default WashAndFoldPage;
